import { observer } from "mobx-react";
import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { FormattedMessage } from "react-intl";

const ImageCardCMS = (props: any) => {
  const {
    id,
    index,
    images,
    handleDeleteImage,
    handleShowPreview,
    handleShowEditor,
    viewOnly,
    children,
    addImage,
  } = props;

  const onMouseEnter = (e: any) => {
    const overlay = e.currentTarget.querySelector(
      ".image-card-overlay"
    ) as HTMLElement | null;
    if (overlay) overlay.style.opacity = "1";
  };

  const onMouseLeave = (e: any) => {
    const overlay = e.currentTarget.querySelector(
      ".image-card-overlay"
    ) as HTMLElement | null;
    if (overlay) overlay.style.opacity = "0";
  };

  const imageUrl = index === -1 ? images[0] : images[index];

  return (
    <div
      key={id}
      className="image-card-container"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={addImage}
    >
      {imageUrl ? (
        <>
          <img
            src={imageUrl.ThumbnailImage}
            alt={`image ${index + 1}`}
            className="image-card-img"
          />
          <div className="image-card-overlay">
            {/* Eye icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="white"
              width="20"
              height="20"
              className="image-card-icon"
              onClick={() => handleShowPreview(index)}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>

            {!viewOnly && (
              <>
                {/* Trash icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="white"
                  width="20"
                  height="20"
                  className="image-card-icon"
                  onClick={() => handleDeleteImage(index)}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </>
            )}
          </div>
        </>
      ) : (
        <span className="image-card-placeholder">{children}</span>
      )}
    </div>
  );
};

interface ImageCardDnDProps {
  id: string;
  type: string;
  index: number;
  moveCard: (from: number, to: number) => void;
  images: string[];
  handleDeleteImage?: (indexToDelete: number) => void;
  handleShowPreview?: (index: number) => void;
  handleShowEditor?: (index: number) => void;
  addImage?: () => void;
  isAddImage?: boolean;
  viewOnly: boolean;
  children?: any;
}

const ImageCardDnD: React.FC<ImageCardDnDProps> = ({
  id,
  type,
  index,
  moveCard,
  images,
  handleDeleteImage,
  handleShowPreview,
  handleShowEditor,
  addImage,
  isAddImage,
  viewOnly = false,
  children,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const hasImage = Boolean(images[index]);

  const [, drop] = useDrop({
    accept: type,
    canDrop: () => !viewOnly && hasImage,
    drop(item: { index: number }) {
      const from = item.index;
      const to = index;
      if (from !== to && hasImage && images[from]) {
        moveCard(from, to);
        item.index = to;
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    canDrag: () => !viewOnly && hasImage,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Only apply drag-drop when not viewOnly
  if (!viewOnly) {
    drag(drop(ref));
  }

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <ImageCardCMS
        id={id}
        index={index}
        images={images}
        handleDeleteImage={handleDeleteImage}
        handleShowPreview={handleShowPreview}
        handleShowEditor={handleShowEditor}
        addImage={addImage}
        viewOnly={viewOnly}
      >
        {children || isAddImage ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="45"
            height="45"
            fill="currentColor"
            className="bi bi-plus-lg"
            viewBox="0 0 16 16"
          >
            <path
              fill-rule="evenodd"
              d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"
            />
          </svg>
        ) : (
          <FormattedMessage id="empty" />
        )}
      </ImageCardCMS>
    </div>
  );
};

export default observer(ImageCardDnD);
